import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';

const NO_ANSWER = new Set([0, 502, 503, 504]);

/** Fehlertext einer Aktion; ohne Antwort ist offen, ob sie gewirkt hat — der Stand lädt neu. */
async function failure(error: unknown, action: string): Promise<string> {
  const problem = await toApiError(error);
  return NO_ANSWER.has(problem.status)
    ? `Keine Antwort vom Server – ob ${action}, zeigt der neu geladene Stand.`
    : problemText(problem, 'Das hat nicht geklappt.');
}

/**
 * Antworten, Schließen und „Keine Antwort nötig“ einer Unterhaltung — für die geteilte Ansicht und
 * den Direktlink dieselbe Fassung.
 */
export function useConversationActions(id: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['inbox'] });
    void qc.invalidateQueries({ queryKey: ['operations'] });
  };

  const reply = useMutation({
    mutationFn: (text: string) => api.messaging.reply(id!, { message: text }),
    networkMode: 'always',
    onSettled: refresh,
  });

  const close = useMutation({
    mutationFn: () => api.messaging.close(id!),
    networkMode: 'always',
    onSuccess: () => toast.success('Unterhaltung geschlossen'),
    onError: async (error) => toast.error(await failure(error, 'sie geschlossen ist')),
    onSettled: refresh,
  });

  // Ein reines Signal an Booking.com: die Unterhaltung bleibt in „Nicht beantwortet“, bis sie
  // geschlossen oder beantwortet ist. Das sagt die Meldung, damit niemand auf ihr Verschwinden wartet.
  const noReply = useMutation({
    mutationFn: () => api.messaging.noReplyNeeded(id!),
    networkMode: 'always',
    onSuccess: () =>
      toast.success(
        'Booking.com weiß Bescheid. In „Nicht beantwortet“ bleibt sie, bis du sie schließt.',
      ),
    onError: async (error) => toast.error(await failure(error, 'Booking.com Bescheid weiß')),
  });

  return { reply, close, noReply };
}
